import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Menu, HelpCircle, ShoppingCart } from 'lucide-react';
import { toggleSidebar } from '@store/slices/uiSlice';
import { useAuth } from '@hooks/useAuth';
import { useTour } from '@contexts/TourContext';
import { useMentorTour } from '@contexts/MentorTourContext';
import { ROUTES, USER_ROLES } from '@constants';
import Button from '@components/common/Button';
import FowBrand from '@components/common/FowBrand';
import NotificationDropdown from '@components/common/NotificationDropdown';
import Avatar from '@components/common/Avatar';

const Header = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout, user, refreshUser } = useAuth();
  const { startTour } = useTour();
  const { startTour: startMentorTour } = useMentorTour();

  // Cart item count from Redux store — adjust selector path if your cart slice differs
  const cartItemCount = useSelector((state) => state.cart?.items?.length || 0);

  // Refresh user data on mount to ensure profile picture is up to date
  useEffect(() => {
    if (isAuthenticated) {
      refreshUser();
    }
  }, [isAuthenticated, refreshUser]);

  const getPageTitle = () => {
    const path = location.pathname;

    if (path.includes('/portfolio/') && path !== ROUTES.STUDENT_PORTFOLIO) {
      return 'Explore Portfolios';
    }

    // Students Directory route - check before /student paths
    if (path === '/students') {
      return 'PORTFOLIOS';
    }

    // Check for My Questions page first
    if (path === ROUTES.STUDENT_MY_QUESTIONS) {
      return 'HISTORY';
    }

    // Check for Mentor page
    if (path.startsWith('/student/mentor')) {
      return 'MENTOR';
    }

    // Check for Requests page
    if (path === ROUTES.STUDENT_REQUESTS) {
      return 'REQUESTS';
    }

    // All /student and /student/* paths show DASHBOARD
    if (path === ROUTES.STUDENT_DASHBOARD || path.startsWith('/student')) {
      return 'DASHBOARD';
    }

    // Admin routes - specific page titles
    if (path.startsWith('/admin')) {
      if (path === '/admin' || path === '/admin/dashboard') return 'Admin Dashboard';
      if (path.startsWith('/admin/mentors')) return 'Mentor Management';
      if (path.startsWith('/admin/students')) return 'Student Management';
      if (path.startsWith('/admin/payments')) return 'Payments Management';
      if (path.startsWith('/admin/subscriptions')) return 'Subscription History';
      if (path.startsWith('/admin/mentor-balances')) return 'Mentor Account Balances';
      if (path.startsWith('/admin/feedback')) return 'Feedback Management';
      if (path.startsWith('/admin/support')) return 'Support Tickets';
      // if (path.startsWith('/admin/analytics')) return 'z';
      return 'Admin Panel';
    }

    // Support routes
    if (path.startsWith('/support')) {
      return 'Support Tickets';
    }

    switch (path) {
      case ROUTES.HOME:            return 'HOME';
      case ROUTES.PROFILE:         return 'PROFILE';
      case ROUTES.SETTINGS:        return 'SETTINGS';
      case ROUTES.CART:            return 'CART';
      case ROUTES.CART_VIEW:       return 'MY CART';
      case ROUTES.HISTORY:         return 'HISTORY';
      case ROUTES.NOTES:           return 'NOTES';
      case ROUTES.PAYMENT_HISTORY: return 'PAYMENT HISTORY';
      case '/notifications':       return 'NOTIFICATIONS';
      default:                     break;
    }

    // Mentor routes - specific page titles
    if (path.startsWith('/mentor')) {
      if (path === '/mentor' || path === '/mentor/dashboard') return 'DASHBOARD';
      if (path === '/mentor/mentors') return 'MENTORS';
      if (path === '/mentor/earnings') return 'EARNINGS';
      if (path === '/mentor/notifications') return 'NOTIFICATIONS';
      if (path === '/mentor/questions') return 'QUESTIONS';
      if (path === '/mentor/feedback') return 'FEEDBACK';
      if (path === '/mentor/videos') return 'VIDEOS';
      if (path === '/mentor/profile') return 'PROFILE';
      if (path.startsWith('/mentor/profile/')) return 'MENTOR PROFILE';
      return 'DASHBOARD';
    }

    return 'HOME';
  };

  return (
    /* Sticky inside the flex column — does NOT overlap sidebar */
    <header className="sticky top-0 z-40 h-14 sm:h-16 bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
      <div className="h-full flex items-center justify-between gap-2 px-3 sm:px-4 md:px-6">

        {/* Left: Mobile hamburger + Page title */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
          {isAuthenticated && (
            <button
              onClick={() => dispatch(toggleSidebar())}
              className="lg:hidden w-9 h-9 -ml-1 sm:-ml-2 rounded-full flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>
          )}

          {isAuthenticated && (
            <h1
              className="min-w-0 truncate text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 tracking-wide"
              title={getPageTitle()}
            >
              {getPageTitle()}
            </h1>
          )}

          {!isAuthenticated && (
            
             <a href="https://www.flatoutway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-lg sm:text-2xl md:text-3xl font-bold text-navbar tracking-wide truncate"
            >
              <FowBrand size="xsmall" />
            </a>
          )}
        </div>

        {/* Right: Help + Notifications + Profile */}
        <div className="flex items-center gap-1.5 sm:gap-2.5 flex-shrink-0">
          {isAuthenticated ? (
            <>
              {/* Tour / Help Button — fixed circle, centered icon */}
              <button
                onClick={() => {
                  // Check user role and start appropriate tour
                  if (user?.role === USER_ROLES.MENTOR) {
                    startMentorTour();
                  } else {
                    startTour();
                  }
                }}
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
                aria-label="Start Tour"
                title="Take a tour"
              >
                <HelpCircle className="w-5 h-5 text-gray-600" />
              </button>


              {/* Cart — standard e-commerce style icon with item-count badge (only for students) */}
              {user?.role === USER_ROLES.STUDENT && (
                <Link
                  to={ROUTES.CART_VIEW}
                  className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
                  aria-label="Cart"
                  title="Cart"
                >
                  <ShoppingCart className="w-5 h-5 text-gray-600" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] leading-[18px] font-semibold text-center ring-2 ring-white">
                      {cartItemCount > 99 ? '99+' : cartItemCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Notification bell — forced into the same circular footprint */}
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0 [&>button]:w-full [&>button]:h-full [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:rounded-full">
                <NotificationDropdown />
              </div>

              {/* Profile Avatar — perfect circle, image cropped/cover, no stretch */}
              <button
                onClick={() => navigate(ROUTES.PROFILE)}
                className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden ring-2 ring-gray-300 hover:ring-primary-500 transition-all flex-shrink-0 bg-gray-100"
                aria-label="Profile"
                title="View Profile"
              >
                 <div className="absolute inset-0 flex items-center justify-center [&_img]:absolute [&_img]:inset-0 [&_img]:w-full [&_img]:h-full [&_img]:object-cover [&_img]:object-center [&_img]:rounded-full">
                    <Avatar
                      src={user?.profilePicture}
                      name={`${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                      email={user?.email}
                      size="sm"
                      showBorder={false}
                      className="w-full h-full object-cover rounded-full"
                    />
                </div>
              </button>
            </>
          ) : (
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Link to={ROUTES.LOGIN}>
                <Button variant="ghost" size="md" className="!px-2 sm:!px-2.5 !py-1 !text-xs sm:!text-sm md:!text-base whitespace-nowrap">Login</Button>
              </Link>
              <Link to={ROUTES.REGISTER}>
                <Button variant="primary" size="md" className="!px-2 sm:!px-2.5 !py-1 !text-xs sm:!text-sm md:!text-base whitespace-nowrap">Sign Up</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;